// Lib
import * as Rematrix from 'rematrix';

// Types
import { Point } from '../../../common/maths/geometry/pointTypes';

// Utils
import platformSingleton from '../../platform/platformSingleton';
import * as matrixLib from '../../../common/maths/matrix/matrixUtils';

/**
 * Creates an appropriate transformation matrix in order to scale the canvas from a specific point
 * with an initial transformation matrix applied.
 *
 * See this board for details:
 * https://app.milanote.com/1OpZDW1aT6xl1p/maths?p=Tsw3W5RIKgd
 */
export const getUpdatedTransformationMatrix = (
    targetScale: number,
    transformationPoint: Point,
    transformationMatrix: Rematrix.Matrix3D,
    canvasScroll: Point,
): Rematrix.Matrix3D => {
    const currentScale = matrixLib.getScale(transformationMatrix);
    const scaleDelta = targetScale / currentScale;

    const translateToOriginMatrix = Rematrix.translate(-transformationPoint.x, -transformationPoint.y);
    const scaleMatrix = Rematrix.scale(scaleDelta);
    const translateBackMatrix = Rematrix.inverse(translateToOriginMatrix);

    // NOTE: Order here is important - multiplication occurs from right to left
    //  First   - Start off with the existing transformation
    //  Second  - translate to the origin
    //  Third   - scale
    //  Fourth  - translate back
    const productArray = [translateBackMatrix, scaleMatrix, translateToOriginMatrix, transformationMatrix];

    let updatedMatrix = productArray.reduce(Rematrix.multiply);

    // Sometimes, due to JS multiplication the scale doesn't exactly match the target scale, so just force it here.
    // This is especially important when returning to 100%
    updatedMatrix = matrixLib.setScale(updatedMatrix, targetScale);

    // To prevent a "bobble" when returning to 100% scale we need to make sure our values are rounded, because
    // the translation will be replaced with scroll which must be an integer
    if (targetScale === 1) {
        updatedMatrix = matrixLib.roundTranslation(updatedMatrix, platformSingleton.features.isRetina ? 0.5 : 1);
    }

    // If we're going from zoomed out back to 100% then the translation must be less than or equal to the canvas scroll,
    // otherwise there will be a slight gap/padding shown on return to 100%, that will subsequently be removed
    // when we replace the translation with scroll in ZoomCanvas.fixCanvasSizeAndScroll
    if (targetScale === 1 && currentScale < 1) {
        updatedMatrix = matrixLib.limitTranslation(updatedMatrix, canvasScroll);
    }

    return updatedMatrix;
};
